import { Badge, Box, Divider, Grid, SIZE, Stack, Text } from '@quinyx/ui';

import {
  EMPTY_VALUE,
  TASK_STATE_BADGE_VARIANT,
} from '@/constants/support-task';
import { ISupportTask } from '../../api/interfaces/support/task';
import { useTranslate } from '../../hooks/translate';
import { CopyableValue, TaskField } from './TaskField';
import { getTaskState, humanDate } from './deleteTaskHelpers';

type TaskSummaryProps = {
  task: ISupportTask;
};

const TaskSummary = ({ task }: TaskSummaryProps) => {
  const { t } = useTranslate();
  const {
    _id: taskId,
    name,
    description,
    isAnnouncement,
    scheduledFor,
    distributedTotal,
    distributedAt,
    createdAt,
    createdBy,
    group,
  } = task;
  const state = getTaskState(task);

  return (
    <Box border borderRadius="l" p={SIZE.M} backgroundColor="SurfaceMain">
      <Stack spacing={SIZE.M}>
        <Stack spacing={SIZE.XS}>
          <Stack direction="row" spacing={SIZE.S} alignItems="center" flexWrap>
            <Text variant="headlineHeavy">{name || EMPTY_VALUE}</Text>
            <Badge
              data-test-id="support-delete-task-state-tag"
              label={t(`support.deleteTask.state.${state}`)}
              variant={TASK_STATE_BADGE_VARIANT[state]}
            />
            <Badge
              label={
                isAnnouncement
                  ? t('support.deleteTask.announcement')
                  : t('support.deleteTask.task')
              }
              variant="displayMonsoon"
            />
          </Stack>
          {description && (
            <Text variant="smallRegular" color="SurfaceOnSurfaceAlt">
              {description}
            </Text>
          )}
        </Stack>
        <Grid columns={[1, 2, 3]} gap={SIZE.M}>
          <TaskField label={t('support.deleteTask.distributedTo')}>
            {distributedTotal ?? 0}
          </TaskField>
          {distributedAt && (
            <TaskField label={t('support.deleteTask.lastSent')}>
              {humanDate(distributedAt)}
            </TaskField>
          )}
          {scheduledFor && (
            <TaskField label={t('support.deleteTask.scheduled')}>
              {humanDate(scheduledFor)}
            </TaskField>
          )}
          <TaskField label={t('support.deleteTask.createdAt')}>
            {humanDate(createdAt) || EMPTY_VALUE}
          </TaskField>
        </Grid>
        <Divider />
        <Grid columns={[1, 1, 3]} gap={SIZE.M}>
          <TaskField label={t('support.deleteTask.taskId')}>
            <CopyableValue
              value={taskId}
              testId="support-delete-task-copy-task-id"
            />
          </TaskField>
          <TaskField label={t('support.deleteTask.createdBy')}>
            <CopyableValue
              value={createdBy}
              testId="support-delete-task-copy-created-by"
            />
          </TaskField>
          <TaskField label={t('support.deleteTask.group')}>
            <CopyableValue
              value={group}
              testId="support-delete-task-copy-group"
            />
          </TaskField>
        </Grid>
      </Stack>
    </Box>
  );
};

export default TaskSummary;
